// AETHER client — tries real worker first, falls back to excellent local engine.
// This is what makes the app actually usable in a real civic emergency.

import { nanoid } from 'nanoid'
import type { Kit, Participant, Node, ClarityCard, Artifact, CalibrationGraphData, ExperimentVariant, PublicArtifactView, TelemetryEvent } from '../types'
import * as local from './localStore'

const API_BASE = '' // same origin (Pages Functions or Worker assets fallback)

async function tryFetch<T>(path: string, init?: RequestInit): Promise<T | null> {
  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers: { 'Content-Type': 'application/json', ...(init?.headers || {}) }
    })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    return await res.json() as T
  } catch {
    return null
  }
}

// --- Public artifact (no auth, works even if worker is only partially deployed) ---
export async function getPublicArtifact(shareCode: string): Promise<PublicArtifactView | null> {
  const localArt = local.getArtifactByCode(shareCode)
  if (localArt) {
    return {
      artifact: { title: localArt.title, type: localArt.artifact_type, created_at: localArt.created_at },
      payload: JSON.parse(localArt.snapshot_json)
    }
  }
  const remote = await tryFetch<PublicArtifactView>(`/api/artifacts/${shareCode}`)
  return remote
}

// --- Kit lifecycle ---
export async function createKit(input: { name: string; context: string; horizon: string; display_name?: string }): Promise<{ kit: Kit; participant: Participant; code: string }> {
  const remote = await tryFetch<{ kit: Kit; participant: Participant; code: string }>('/api/kits', {
    method: 'POST',
    body: JSON.stringify(input)
  })
  if (remote) return remote

  // Local
  const kit = local.createLocalKit(input.name, input.context, input.horizon)
  if (input.display_name) local.setDisplayNameForKit(kit.id, input.display_name)
  const pid = local.getStableParticipantId()
  const p: Participant = { id: pid, kit_id: kit.id, display_name: local.getDisplayNameForKit(kit.id), joined_at: Date.now(), last_seen: Date.now() }
  local.setCurrentKit(kit.id)
  return { kit, participant: p, code: kit.code }
}

export async function joinKit(code: string, display_name?: string): Promise<{ kit: Kit; participant: Participant }> {
  const remote = await tryFetch<{ kit: Kit; participant: Participant }>(`/api/kits/join`, {
    method: 'POST',
    body: JSON.stringify({ code: code.toUpperCase(), display_name })
  })
  if (remote) {
    local.setCurrentKit(remote.kit.id)
    if (display_name) local.setDisplayNameForKit(remote.kit.id, display_name)
    return remote
  }
  // Local
  const kit = local.getKit(code)
  if (!kit) throw new Error('Kit not found on this device')
  if (display_name) local.setDisplayNameForKit(kit.id, display_name)
  const p: Participant = {
    id: local.getStableParticipantId(),
    kit_id: kit.id,
    display_name: local.getDisplayNameForKit(kit.id),
    joined_at: Date.now(),
    last_seen: Date.now()
  }
  local.setCurrentKit(kit.id)
  return { kit, participant: p }
}

export async function getKit(kitId: string): Promise<Kit | null> {
  const remote = await tryFetch<Kit>(`/api/kits/${kitId}`)
  if (remote) return remote
  return local.getKit(kitId)
}

// --- Nodes (the heart of the instrument) ---
export async function listNodes(kitId: string): Promise<Node[]> {
  const remote = await tryFetch<Node[]>(`/api/kits/${kitId}/nodes`)
  if (remote) return remote
  return local.getNodesForKit(kitId)
}

export async function createNode(kitId: string, data: { description: string; parent_id?: string | null; confidence?: number; confidence_basis?: string; burden_expected?: number | null; created_by: string }): Promise<Node> {
  const remote = await tryFetch<Node>(`/api/kits/${kitId}/nodes`, { method: 'POST', body: JSON.stringify(data) })
  if (remote) return remote
  return local.addNode(kitId, data)
}

export async function updateNode(kitId: string, nodeId: string, patch: Partial<Node>): Promise<Node | null> {
  const remote = await tryFetch<Node>(`/api/kits/${kitId}/nodes/${nodeId}`, { method: 'PATCH', body: JSON.stringify(patch) })
  if (remote) return remote
  return local.updateNode(kitId, nodeId, patch)
}

export async function abandonNode(kitId: string, nodeId: string, reason: string, participantId: string): Promise<Node | null> {
  const remote = await tryFetch<Node>(`/api/kits/${kitId}/nodes/${nodeId}/abandon`, { method: 'POST', body: JSON.stringify({ reason, participant_id: participantId }) })
  if (remote) return remote
  return local.abandonNode(kitId, nodeId, reason, participantId)
}

// --- Clarity (recovery clarity is sacred) ---
export async function createClarity(kitId: string, data: Omit<ClarityCard, 'id' | 'kit_id' | 'created_at'>): Promise<ClarityCard> {
  const remote = await tryFetch<ClarityCard>(`/api/kits/${kitId}/clarity`, { method: 'POST', body: JSON.stringify(data) })
  if (remote) return remote
  return local.addClarityCard(kitId, data)
}

export async function listClarity(kitId: string): Promise<ClarityCard[]> {
  const remote = await tryFetch<ClarityCard[]>(`/api/kits/${kitId}/clarity`)
  if (remote) return remote
  return local.getClarityCards(kitId)
}

// --- Graph ---
export async function getCalibrationGraph(kitId: string): Promise<CalibrationGraphData> {
  const remote = await tryFetch<CalibrationGraphData>(`/api/kits/${kitId}/graph`)
  if (remote) return remote
  return local.computeCalibrationGraph(kitId)
}

// --- Explicit share (the privacy model) ---
export async function forgeArtifact(kitId: string, forgerId: string, type: Artifact['artifact_type'], title: string, snapshot: any): Promise<Artifact> {
  const remote = await tryFetch<Artifact>(`/api/kits/${kitId}/artifacts`, { method: 'POST', body: JSON.stringify({ forger_id: forgerId, artifact_type: type, title, snapshot }) })
  if (remote) return remote
  return local.forgeArtifact(kitId, forgerId, type, title, snapshot)
}

export async function listArtifacts(kitId: string): Promise<Artifact[]> {
  const remote = await tryFetch<Artifact[]>(`/api/kits/${kitId}/artifacts`)
  if (remote) return remote
  return local.getArtifacts(kitId)
}

// --- AI-assisted (grounded + vector retrieval when available) ---
export async function aiDecompose(kitId: string, situation: string): Promise<Array<{ description: string; starterConfidence: number; basis: string; burden: number }>> {
  const remote = await tryFetch<any>(`/api/ai/decompose`, { method: 'POST', body: JSON.stringify({ kit_id: kitId, situation }) })
  if (remote?.suggestions) return remote.suggestions
  return local.localDecompose(situation)
}

export async function aiProbe(nodeDescription: string, kitContext?: string): Promise<string[]> {
  const remote = await tryFetch<{ probes: string[] }>(`/api/ai/probe`, { method: 'POST', body: JSON.stringify({ node: nodeDescription, context: kitContext }) })
  if (remote?.probes?.length) return remote.probes
  const v = local.getLocalVariant()
  return local.localProbes(nodeDescription, v)
}

// --- Variants (the stochastic heart) ---
export async function getVariant(kitId?: string): Promise<ExperimentVariant> {
  const remote = await tryFetch<ExperimentVariant>(`/api/experiments/variant?kit=${kitId || ''}`)
  if (remote) return remote
  return local.getLocalVariant()
}

// --- Telemetry (also hits Analytics Engine via worker) ---
export async function emitTelemetry(ev: TelemetryEvent) {
  // fire and forget; local always records
  local.appendEvent(ev.kit_id || 'local', ev.participant_id || 'local', ev.node_id || null, ev.type, ev.payload)
  tryFetch('/api/telemetry', { method: 'POST', body: JSON.stringify(ev) }).catch(() => {})
}

// Convenience: seed the canonical demo
export { seedDemoFloodKit } from './localStore'
